<template>
  <section
    class="popular-searches scrollable"
    :class="{ desktop: $isDesktop }"
    :style="{ gridTemplateColumns: `repeat(${columnCount}, 34vw)` }"
  >
    <nuxt-link
      v-for="(card, index) of popularSearches"
      :to="safeLocalePath({ path: card.url })"
      :style="{
        order: card.order && !$isDesktop ? card.order : 20,
      }"
      @click="emit('popular-search-clicked')"
    >
      <div v-if="withImage" class="picture">
        <img
          :fetchpriority="index === 0 ? 'high' : undefined"
          :loading="isLazyImg(index) ? 'lazy' : undefined"
          :src="
            $isDesktop
              ? `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=65,w=224,h=192${card.image}`
              : `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=65,w=140,h=115${card.image}`
          "
          :alt="card.name"
          width="140"
          height="115"
        />
      </div>
      <span v-if="withImage">{{ card.name }}</span>
      <template v-else>
        {{ card.name }}
      </template>
    </nuxt-link>
  </section>
</template>

<script setup lang="ts">
import { getLangFallbackUrl } from '~/lang/utils/langfetch'

interface PopularSearch {
  url: string
  name: string
  image: string
  order: number
}

withDefaults(
  defineProps<{
    withImage?: boolean
  }>(),
  {
    withImage: true,
  },
)
const emit = defineEmits<(e: 'popular-search-clicked') => void>()

// Safe wrapper for $puLocalePath
const safeLocalePath = (route: any): string => {
  const nuxtApp = useNuxtApp()
  if (nuxtApp.$puLocalePath) {
    return nuxtApp.$puLocalePath(route)
  }
  // Fallback to basic path
  return route.path || '/'
}
const langCode = (useRoute().params?.lang as string) || 'en-gb'

const { data: popularSearches } = await useAsyncData(
  `popularSearches-${langCode}`,
  () =>
    $fetch<PopularSearch[]>(
      getLangFallbackUrl(langCode, '/_/popular-searches/'),
    ),
  // avoid duplicate requests server and client
  {
    server: true,
    lazy: process.client, // Defer fetching until client-side execution
    dedupe: 'cancel',
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

const columnCount = computed(() => {
  return Math.round((popularSearches.value?.length ?? 0) / 2)
})

const isLazyImg = (index: number): boolean => {
  return index > 5
}
</script>

<style scoped>
.popular-searches {
  column-gap: 0.7em;
  display: grid;
  grid-auto-rows: minmax(28vw, auto);
  row-gap: 0.7em;
  opacity: 1;
}

a {
  box-sizing: content-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
  text-decoration: none;
  position: relative;
}

a span {
  font-size: var(--body);
  color: white;
  position: absolute;
  bottom: 0.7em;
  left: 0.7em;
  display: block;
  padding-right: 1.5em;
  font-weight: bold;
  line-height: 1.15;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.75);
}

.picture {
  overflow: hidden;
  height: 28vw;
  border-radius: 0.65em;
  background-color: var(--colour-page-background);
  position: relative;
}

.picture img {
  width: auto;
  height: 100%;
  max-width: none;
  aspect-ratio: auto 4/3;
}

.picture:before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0) 50%
  );
}

/* desktop & tablet */
.popular-searches.desktop {
  grid-template-columns: repeat(8, 14em) !important;
  column-gap: 3em;
  grid-auto-rows: 12em;
  row-gap: 3em;
  margin: 0;
}

.desktop a {
  transition:
    box-shadow 0.15s ease-out,
    transform 0.25s ease-out;
  border-radius: 1em;
}

.desktop a:hover,
.desktop a:focus-visible {
  box-shadow: 0 0 0 7px var(--focus-ring-colour);
  transform: scale(1.05);
  outline: none;
}

.desktop a span {
  font-size: 1.3em;
}

.desktop .picture {
  height: 100%;
}
</style>
